import React from 'react';
import CategoryDonut from '../CategoryDonut.js';
import { computeTreatmentCostPerM3 } from '../calculations/reutOpexCalculations.js';
import { ROLE_COLORS } from '../calculations/reutRoles.js';

const LIMITING_LABELS = {
  gisement: 'bridé par le gisement disponible',
  besoin: 'bridé par le besoin des usages sélectionnés',
  capacité: 'bridé par la capacité de traitement du modèle',
};

/**
 * Étape 3 : synthèse — répartition de la consommation du bâtiment par catégorie (donut,
 * coloriée par rôle REUT : gisement / débouché / hors périmètre), et choix du modèle Aquapod.
 * Le volume réellement valorisable pour chaque modèle = min(gisement × rendement 80%, besoin,
 * capacité annuelle du modèle) — cf. balanceCalculations.js. Le coût de traitement par modèle
 * est affiché directement dans le tableau, en regard du prix réseau, pour la même question
 * business qu'à l'étape Budget (rentable ou non), mais visible dès le choix du modèle.
 */
const BalanceStep = ({
  categoryBreakdown, gisementVolume, besoinVolume,
  evaluations, recommendedModelId, networkPrice,
  selectedModelId, onSelectModel,
  currentStep, totalSteps, goPrevious, goNext, canAdvance,
}) => (
  <div className="apv2-step-card">
    <div className="apv2-step-header">
      <div className="apv2-step-header-text">
        <span className="apv2-step-eyebrow">Réutilisation des eaux grises</span>
        <h2 className="apv2-step-heading">Bilan et dimensionnement</h2>
      </div>
      <span className="apv2-step-counter">{currentStep + 1} / {totalSteps}</span>
    </div>
    <div className="apv2-step-body">

      <p className="apv2-sim-hint">
        Répartition de la consommation totale du bâtiment par catégorie d'usage — en bleu les catégories
        retenues comme gisement d'eau grise (étape 1), en doré les débouchés valorisables (étape 2).
      </p>
      <CategoryDonut categories={categoryBreakdown} />

      <div className="apv2-summary-cards">
        <div className="apv2-summary-card gw-summary-card--role" style={{ '--gw-role-color': ROLE_COLORS.gisement }}>
          <span className="apv2-summary-value">{gisementVolume.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³</span>
          <span className="apv2-summary-label">gisement d'eau grise (étape 1)</span>
        </div>
        <div className="apv2-summary-card gw-summary-card--role" style={{ '--gw-role-color': ROLE_COLORS.besoin }}>
          <span className="apv2-summary-value">{besoinVolume.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³</span>
          <span className="apv2-summary-label">besoin des débouchés (étape 2)</span>
        </div>
      </div>

      <p className="apv2-sim-hint">
        Le volume réellement valorisable dépend aussi de la capacité de traitement du modèle Aquapod choisi
        ci-dessous — le débit journalier (m³/j) multiplié par 365 jours/an.
      </p>

      <div className="rw-sub-label" style={{ marginTop: 20 }}>Choix du modèle Aquapod</div>
      <p className="gw-price-ref">Prix de l'eau du réseau : <b>{networkPrice.toFixed(2)} €/m³</b> (modifiable à l'étape Budget).</p>
      <div className="apv2-flat-table-wrapper">
        <table className="apv2-flat-table">
          <thead>
            <tr>
              <th>Modèle</th>
              <th>Capacité annuelle</th>
              <th>Volume valorisable</th>
              <th>Facteur limitant</th>
              <th>Coût eau traitée</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {evaluations.map(({ model, capacity, valorized, limitingFactor }) => {
              const costPerM3 = computeTreatmentCostPerM3(valorized);
              const isViable = costPerM3 != null && costPerM3 < networkPrice;
              return (
                <tr key={model.id} className={selectedModelId === model.id ? 'apv2-flat-table-row--selected' : ''}>
                  <td>
                    {model.label}
                    {recommendedModelId === model.id && <span className="gw-recommended-badge">Recommandé</span>}
                  </td>
                  <td>{capacity.toLocaleString('fr-FR')} m³/an</td>
                  <td>{valorized.toLocaleString('fr-FR', { maximumFractionDigits: 1 })} m³/an</td>
                  <td>{LIMITING_LABELS[limitingFactor]}</td>
                  <td className={costPerM3 == null ? '' : isViable ? 'gw-model-cost--ok' : 'gw-model-cost--bad'}>
                    {costPerM3 != null ? `${costPerM3.toFixed(2)} €/m³` : '—'}
                  </td>
                  <td>
                    <button type="button" className="rw-add-btn" onClick={() => onSelectModel(model.id)}>
                      {selectedModelId === model.id ? 'Modèle choisi ✓' : 'Choisir ce modèle'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
    <div className="apv2-step-footer">
      <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
        ← Précédent
      </button>
      <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
        Suivant →
      </button>
    </div>
  </div>
);

export default BalanceStep;
